import { useEffect, useRef, useState } from 'react';
import { NEUROTOX_PULSE_DURATION_MS } from '../config/neurotoxPulseEffect';

function mapNeurotoxToViewerSlot(fx, viewerUserId) {
  if (!fx || fx.zone == null || fx.slotIndex == null || fx.targetPlayerId == null) {
    return null;
  }
  const isOwnerViewer = String(fx.targetPlayerId) === String(viewerUserId);
  const prefix = isOwnerViewer ? 'your' : 'opponent';
  return {
    zone: `${prefix}_${fx.zone}`,
    index: fx.slotIndex,
  };
}

const SHRINK_CHEVRON_STRATEGEM_IDS = new Set([53, 180, 183]);

function isNeurotoxPulseStrategem(s) {
  if (!s || s.instanceId == null) return false;
  if (!SHRINK_CHEVRON_STRATEGEM_IDS.has(Number(s.id))) return false;
  const t = (s.type && String(s.type)) || '';
  if (!t) return true;
  return /strategem/i.test(t);
}

/**
 * Strategems 53 (Neurotox Pulse), 180 (Touch of darkness), 183 (Growing Darkness):
 * same `neurotoxPulseFx` slot + NeurotoxPulseEffect chevron shrink (sounds differ per useStrategemSoundEffects).
 */
export function useNeurotoxPulse(gameState, viewerUserId) {
  const [activeNeurotoxPulses, setActiveNeurotoxPulses] = useState([]);
  const processedRef = useRef(new Set());
  const seededRef = useRef(false);
  const gameIdRef = useRef(null);

  useEffect(() => {
    if (!gameState?.state || viewerUserId == null) return;

    const gid = gameState.gameId ?? null;
    if (gid != null && gameIdRef.current !== gid) {
      gameIdRef.current = gid;
      seededRef.current = false;
      processedRef.current = new Set();
      setActiveNeurotoxPulses([]);
    }

    if (!seededRef.current) {
      Object.keys(gameState.state).forEach((pid) => {
        gameState.state[pid]?.completedOrders?.forEach((st) => {
          if (isNeurotoxPulseStrategem(st)) {
            processedRef.current.add(`ntox${st.id}-${pid}-${st.instanceId}`);
          }
        });
      });
      seededRef.current = true;
      return;
    }

    Object.keys(gameState.state).forEach((casterId) => {
      const player = gameState.state[casterId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach((strategem) => {
        if (!isNeurotoxPulseStrategem(strategem)) return;
        const fx = strategem.neurotoxPulseFx;
        if (!fx) return;

        const dedupeKey = `ntox${strategem.id}-${casterId}-${strategem.instanceId}`;
        if (processedRef.current.has(dedupeKey)) return;
        processedRef.current.add(dedupeKey);

        const viewerSlot = mapNeurotoxToViewerSlot(fx, viewerUserId);
        if (!viewerSlot) return;

        const animKey = `ntox${strategem.id}-${strategem.instanceId}-${Date.now()}`;
        setActiveNeurotoxPulses((prev) => [
          ...prev,
          { key: animKey, viewerSlot, animationKey: animKey },
        ]);

        window.setTimeout(() => {
          setActiveNeurotoxPulses((prev) => prev.filter((p) => p.key !== animKey));
        }, NEUROTOX_PULSE_DURATION_MS + 120);
      });
    });

    if (processedRef.current.size > 120) {
      const keys = Array.from(processedRef.current);
      keys.slice(0, keys.length - 80).forEach((k) => processedRef.current.delete(k));
    }
  }, [gameState, gameState?.stateVersion, viewerUserId]);

  return { activeNeurotoxPulses };
}

export default useNeurotoxPulse;
